import { UploadProps } from 'antd';
import { isNil, merge, throttle } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { atom, useAtomValue } from 'jotai';
import { inputDraftCache2 } from 'services/cache';
import { useAtomCallback } from 'jotai/utils';
import { useGetMessageById } from './message';

export type FileList = Required<UploadProps<any>>['fileList'];

export type ValOrUpdater<T> = ((val: T) => T) | T;

export interface ConversationsDraft {
  input?: string;
  quotedMessageId?: string;
}

type CacheRecord = Record<Conversation.Conversation['conversationId'], ConversationsDraft>;

const updateCache = throttle((next: CacheRecord) => {
  inputDraftCache2.set(next);
}, 800);

const inputDraftAtom = atom(inputDraftCache2.get<CacheRecord>() || {});
export const inputDraftState = atom(
  (get) => get(inputDraftAtom),
  (_, set, next: CacheRecord) => {
    set(inputDraftAtom, next);
    updateCache(next);
  },
);

export const useGetConversationDraftById = () => {
  return useAtomCallback(
    useCallback(async (get, _, conversationId: Conversation.Conversation['conversationId']) => {
      const inputDraft = get(inputDraftState);
      return inputDraft[conversationId];
    }, []),
  );
};

export const useConversationInputDraft = (conversationId: string) => {
  return useAtomValue(inputDraftState)[conversationId]?.input || '';
};

export const useSetConversationInputDraft = () => {
  return useAtomCallback(
    useCallback(
      (get, set, conversationId: string, inputOrUpdater: ValOrUpdater<string> | string) => {
        const inputDraft = get(inputDraftState);
        const { [conversationId]: cur, ...others } = inputDraft;
        let input = '';
        if (typeof inputOrUpdater === 'function') {
          input = inputOrUpdater(cur?.input || '');
        } else {
          input = inputOrUpdater;
        }
        set(inputDraftState, {
          ...others,
          [conversationId]: { ...cur, input },
        });
      },
      [],
    ),
  );
};

export const useClearConverstaionInputDraft = () => {
  return useAtomCallback(
    useCallback((get, set, conversationId?: Conversation.Conversation['conversationId']) => {
      if (!conversationId) {
        return;
      }
      const inputDraft = get(inputDraftState);
      const next = { ...inputDraft };
      delete next[conversationId];
      set(inputDraftState, next);
    }, []),
  );
};

export const useSetConversationQuotedMsgDraft = () => {
  return useAtomCallback(
    useCallback(
      (
        get,
        set,
        conversationId: Conversation.Conversation['conversationId'],
        messageId: string,
      ) => {
        const inputDraft = get(inputDraftState);
        set(
          inputDraftState,
          merge({}, inputDraft, { [conversationId]: { quotedMessageId: messageId } }),
        );
      },
      [],
    ),
  );
};

/**
 * @why useConversationQuotedMsgDraft 直接使用jotai state可以自动化取数过程
 * @why 但是目前在MessageEditor 调用 useConversationQuotedMsgDraft 时，会遇到 messageMapState 更新时
 * @why 整个父节点更新导致某些原生dom被刷新，而丢失状态及事件(focus、输入composing等)
 * @description  因此使用 useCallback 进行自主取数，因为 message 内容不会改变，因此可以忽略 messageMapState 对引用内容的影响
 * @param conversationId
 * @returns
 */
export const useStateSafeConversationQuotedMsgDraft = (conversationId: string) => {
  const draftMsgId = useAtomValue(inputDraftState)[conversationId]?.quotedMessageId || '';
  const [msg, setMsg] = useState<Conversation.Message | null>(null);
  const getMessageById = useGetMessageById();

  const getMsg = useCallback(
    (msgId?: Conversation.Message['msgId']) => {
      if (isNil(msgId)) {
        setMsg(null);
        return;
      }
      setMsg(getMessageById(msgId) ?? null);
    },
    [getMessageById],
  );

  useEffect(() => {
    getMsg(draftMsgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftMsgId]);
  return msg;
};
